import type { AdminUserDetail, User, UserRole } from '@shopgnd/shared';
import { getIdToken } from '@/lib/auth';
import { cognitoAuthFailureMessage } from '@/lib/cognitoSessionHint';
import { resolveAdminUsersUrls } from '@/lib/adminUsersUrl';
import {
  fetchLambdaWithProxyFallback,
} from '@/lib/lambdaFetch';

async function adminFetch<T>(body: Record<string, unknown>): Promise<T> {
  // Prefer admin-api gateway when configured; fall back to admin-users Lambda.
  try {
    const { isAdminApiConfigured, adminUsersAction } = await import('@/api/adminPlatform');
    if (await isAdminApiConfigured()) {
      const action = String(body.action ?? '');
      const { action: _a, ...rest } = body;
      return (await adminUsersAction(action, rest)) as T;
    }
  } catch {
    /* fall through to legacy admin-users */
  }

  const endpoints = await resolveAdminUsersUrls();
  if (!endpoints) throw new Error('Admin user API is not configured.');

  const request = async (forceRefresh: boolean) => {
    const token = await getIdToken({ forceRefresh });
    if (!token) throw new Error('Sign in required.');
    const init = {
      method: 'POST' as const,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    };
    const res = await fetchLambdaWithProxyFallback(endpoints.proxyPath, endpoints.directUrl, init);
    let payload: (T & { error?: string }) | { error?: string };
    try {
      payload = (await res.json()) as T & { error?: string };
    } catch {
      throw new Error(`Admin request failed (${res.status})`);
    }
    return { res, payload, token };
  };

  let { res, payload, token } = await request(false);
  if (res.status === 401) {
    ({ res, payload, token } = await request(true));
  }
  if (!res.ok) {
    const msg =
      res.status === 401
        ? await cognitoAuthFailureMessage(res.status, payload.error, token)
        : (payload.error ?? 'Admin request failed');
    throw new Error(msg);
  }
  return payload as T;
}

export async function listUsersLive(): Promise<User[]> {
  const result = await adminFetch<{ users: User[] }>({ action: 'listUsers' });
  return result.users ?? [];
}

export async function getUserDetailLive(userId: string): Promise<AdminUserDetail> {
  const result = await adminFetch<{ user: AdminUserDetail }>({
    action: 'getUserDetail',
    userId,
  });
  if (!result.user) throw new Error('User not found.');
  return result.user;
}

export async function updateUserProfileLive(
  userId: string,
  patch: { displayName?: string; phone?: string; parish?: string },
): Promise<AdminUserDetail> {
  const result = await adminFetch<{ user: AdminUserDetail }>({
    action: 'updateUserProfile',
    userId,
    ...patch,
  });
  if (!result.user) throw new Error('Profile update failed.');
  return result.user;
}

export async function updateUserRolesLive(
  userId: string,
  roles: UserRole[],
): Promise<User> {
  const result = await adminFetch<{ user: User }>({
    action: 'updateUserRoles',
    userId,
    roles,
  });
  return result.user;
}

export async function suspendUserLive(userId: string): Promise<User> {
  const result = await adminFetch<{ user: User }>({ action: 'suspendUser', userId });
  return result.user;
}

export async function enableUserLive(userId: string, roles?: UserRole[]): Promise<User> {
  const result = await adminFetch<{ user: User }>({
    action: 'enableUser',
    userId,
    ...(roles?.length ? { roles } : {}),
  });
  return result.user;
}

export async function deleteUserLive(userId: string): Promise<void> {
  await adminFetch<{ deleted: boolean; userId: string }>({ action: 'deleteUser', userId });
}

export type ProvisionOwnerFields = {
  email: string;
  displayName: string;
  phone?: string;
  parish?: string;
};

export type ProvisionVendorFields = {
  businessName: string;
  businessOwnerName?: string;
  description?: string;
  logoUrl?: string;
  acceptsCardPayments?: boolean;
  onlineOnly?: boolean;
  offersPickup?: boolean;
  offersDelivery?: boolean;
  pickupAddress?: string;
  pickupParish?: string;
  phone?: string;
  businessModes?: string[];
  primaryMode?: string;
  businessType?: string;
  directorySector?: string;
  directoryCategoryId?: string;
  directoryCategoryCustomLabel?: string;
};

export type ProvisionProFields = {
  headline: string;
  bio?: string;
  categories: string[];
  parish: string;
  hourlyRateCents?: number;
  avatarUrl?: string;
};

export async function createUserLive(params: {
  owner: ProvisionOwnerFields;
  roles?: UserRole[];
  sendWelcomeEmail?: boolean;
}): Promise<{
  userId: string;
  email: string;
  displayName?: string;
  emailSent: boolean;
  emailError?: string;
  passwordSetupUrl?: string;
  passwordSetupExpiresAt?: string;
}> {
  return adminFetch({
    action: 'createUser',
    owner: params.owner,
    roles: params.roles,
    sendWelcomeEmail: params.sendWelcomeEmail,
  });
}

export async function provisionVendorLive(params: {
  owner: ProvisionOwnerFields;
  vendor: ProvisionVendorFields;
  sendWelcomeEmail?: boolean;
}): Promise<{
  userId: string;
  vendorId: string;
  slug: string;
  email: string;
  emailSent: boolean;
  emailError?: string;
  passwordSetupUrl?: string;
  passwordSetupExpiresAt?: string;
}> {
  return adminFetch({
    action: 'provisionVendor',
    owner: params.owner,
    vendor: params.vendor,
    sendWelcomeEmail: params.sendWelcomeEmail,
  });
}

export async function setPlatformAdminLive(
  targetUserId: string,
  grant: boolean,
): Promise<User> {
  const result = await adminFetch<{ user: User }>({
    action: 'setPlatformAdmin',
    targetUserId,
    grant,
  });
  return result.user;
}

export async function listPlatformAdminsLive(): Promise<User[]> {
  const result = await adminFetch<{ users: User[] }>({ action: 'listPlatformAdmins' });
  return result.users ?? [];
}

export async function provisionProLive(params: {
  owner: ProvisionOwnerFields;
  pro: ProvisionProFields;
  sendWelcomeEmail?: boolean;
}): Promise<{
  userId: string;
  proId: string;
  email: string;
  emailSent: boolean;
  emailError?: string;
  passwordSetupUrl?: string;
  passwordSetupExpiresAt?: string;
}> {
  return adminFetch({
    action: 'provisionPro',
    owner: params.owner,
    pro: params.pro,
    sendWelcomeEmail: params.sendWelcomeEmail,
  });
}
